import { reportToastErrorToPosthog } from "@/lib/analytics";
import { defineStore } from "pinia";
import { ref } from "vue";

export type ToastTone = "neutral" | "success" | "error";
export type ToastState = "loading" | "done";

export type ToastItem = {
  id: string;
  title: string;
  description?: string;
  tone: ToastTone;
  state: ToastState;
  autoCloseMs?: number;
};

type PushToastInput = Omit<ToastItem, "id"> & { id?: string };

const DEFAULT_AUTO_CLOSE_MS = 4800;

function makeToastId() {
  return `toast_${Math.random().toString(36).slice(2, 10)}`;
}

export const useToastStore = defineStore("toast", () => {
  const items = ref<ToastItem[]>([]);
  const timers = new Map<string, number>();

  function clearTimer(id: string) {
    const timer = timers.get(id);
    if (!timer) return;
    window.clearTimeout(timer);
    timers.delete(id);
  }

  function scheduleClose(item: ToastItem) {
    clearTimer(item.id);
    if (item.state === "loading") return;
    const timeout = item.autoCloseMs ?? DEFAULT_AUTO_CLOSE_MS;
    const timer = window.setTimeout(() => dismiss(item.id), timeout);
    timers.set(item.id, timer);
  }

  function push(input: PushToastInput): string {
    const id = input.id ?? makeToastId();
    const item: ToastItem = { ...input, id };
    items.value = [...items.value.filter((x) => x.id !== id), item];
    scheduleClose(item);
    if (item.tone === "error") {
      reportToastErrorToPosthog({ id: item.id, title: item.title, description: item.description });
    }
    return id;
  }

  function update(id: string, patch: Partial<Omit<ToastItem, "id">>) {
    const current = items.value.find((x) => x.id === id);
    if (!current) return;
    const next: ToastItem = { ...current, ...patch, id };
    items.value = items.value.map((x) => (x.id === id ? next : x));
    scheduleClose(next);
    if (patch.tone === "error" && current.tone !== "error") {
      reportToastErrorToPosthog({ id: next.id, title: next.title, description: next.description });
    }
  }

  function dismiss(id: string) {
    clearTimer(id);
    items.value = items.value.filter((item) => item.id !== id);
  }

  function clearAll() {
    items.value.forEach((item) => clearTimer(item.id));
    items.value = [];
  }

  return {
    items,
    push,
    update,
    dismiss,
    clearAll,
  };
});
